/**
 * Registry of pages that refuse to be left.
 *
 * A plain module-level set rather than a store or a context: the router
 * interception in `NavigationGuardProvider` reads it inside a click handler
 * and must not re-render the app to do so, and the few call sites that leave
 * the page without the router (logout, full page loads to the admin portal)
 * are not React components.
 */

interface Guard {
  /** Whether this guard refuses a navigation to `href`. */
  blocks: (href?: string) => boolean;
  /** Surfaces the guard's dialog once a navigation has been refused. */
  onBlocked: () => void;
}

const guards = new Set<Guard>();
const navigationListeners = new Set<(href: string) => void>();

export const subscribeToNavigation = (listener: (href: string) => void) => {
  navigationListeners.add(listener);
  return () => {
    navigationListeners.delete(listener);
  };
};

export const notifyNavigation = (href: string) => {
  navigationListeners.forEach((listener) => listener(href));
};

/** Registers a guard and returns its unregister function. */
export const registerGuard = (guard: Guard): (() => void) => {
  guards.add(guard);
  return () => {
    guards.delete(guard);
  };
};

/**
 * Whether the app may navigate to `href`.
 *
 * Pass the target so same-route navigation (a query or hash change, or a link
 * to the page you are already on) is allowed through; omit it for
 * destinations that always leave, such as logout or the back button.
 * Returning false also opens the blocking page's dialog, so callers must bail
 * out rather than navigate anyway.
 */
export const canNavigateAway = (href?: string): boolean => {
  const blocking = [...guards].filter((guard) => guard.blocks(href));
  if (blocking.length === 0) return true;

  blocking.forEach((guard) => guard.onBlocked());
  return false;
};

/** Whether `href` points somewhere other than the page currently open. */
export const leavesCurrentRoute = (href: string): boolean => {
  try {
    const target = new URL(href, window.location.href);
    return (
      target.origin !== window.location.origin ||
      target.pathname !== window.location.pathname
    );
  } catch {
    // Unparseable href — treat as leaving so the guard errs on blocking.
    return true;
  }
};
